import * as Sentry from '@sentry/react'
import axios, { AxiosError } from 'axios'
import { ENV, global_on } from '@/common/constants.ts'
import { ZodError } from 'zod'
import { AuthService } from '@/app/pages/authentication/AuthService'
import { AUTH_MODE } from '@/auth/keycloak'
const global_on_id = global_on.portal
function isAxiosError(error: unknown): error is AxiosError {
  return axios.isAxiosError(error)
}

function shouldLogErrorToSentry(error: unknown): boolean {
  // Always log Zod validation errors as they indicate schema issues
  if (error instanceof ZodError) {
    return true
  }

  // Log 5xx server errors (actual server issues)
  if (isAxiosError(error)) {
    if (error.status && error.status >= 500) {
      return true
    }
  }
  return false
}

export function setupAxios() {
  axios.defaults.headers.Accept = 'application/json'
  axios.defaults.baseURL = `${ENV.VITE_API_URL}/admin`

  axios.interceptors.request.use(
    async (config) => {
      config.params = {
        ...config.params,
        global_on_id: global_on_id,
      }

      const token = await AuthService.getAccessToken()
      if (
        AUTH_MODE === 'legacy' &&
        config.url?.includes('auth/me') &&
        config.headers.Authorization
      ) {
        config.headers.Authorization = `Bearer ${config.headers.Authorization}`
      } else if (
        !config.url?.includes('complete_registration') &&
        token &&
        config?.headers
      ) {
        config.headers.Authorization = `Bearer ${token}`
      }

      const isPublicRoute = config.url?.includes('/public')
      const isSafeProviderRoute =
        config.url?.includes('/providers') &&
        !config.url?.includes('download-file')

      if (isPublicRoute || isSafeProviderRoute) {
        config.baseURL = config.baseURL?.replace('/admin', '')
      }

      return config
    },
    (err) => {
      Sentry.captureException(err)
      return Promise.reject(err)
    }
  )
  axios.interceptors.response.use(
    (response) => {
      return response
    },
    async function (error) {
      try {
        return await AuthService.handleAuthError(error)
      } catch (err) {
        if (shouldLogErrorToSentry(error)) {
          Sentry.captureException(error, {
            extra: {
              message:
                error.response?.data?.message ?? 'No error message provided',
            },
          })
        }
        return Promise.reject(err)
      }
    }
  )

  return {
    post: axios.post,
    get: axios.get,
    put: axios.put,
    delete: axios.delete,
  }
}
